import {Module, createStore} from "vuex";
import {MarkerFeature} from "../../../utils/models/MarkerFeature";
import store, {RootState} from "../store";
import mapboxgl from "mapbox-gl";
import {LineStringFeature} from "../../../utils/models/LineStringFeature";
import {Route, RouteFactory} from "../../../utils/models/Route";
import {UndoManager} from "../undo/UndoManager";
import getters from "./getters";
import mutations from "./mutations";
import actions from "./actions/index";

export class PlanTrailState {
  title: string | null = null
  description: string | null = null
  plannedToHikeOn: string | null = null
  difficultyOverride: string | null = null
  public: boolean = true
  submitForOfficialApproval: boolean = false
  makeOfficial: boolean = false // For trails@hiiker.co to directly set made_official_at
  generateAiDescription: boolean = false // For trails@hiiker.co to trigger AI description generation
  photosIds: string[] = []
  photosToUpload: File[] = []
  photoSignedIds: { [photoId: string]: string } = {}
  photoExistingIds: { [photoId: string]: number } = {}
  photosMarkedForRemoval: string[] = []
  existingHikeImages: Array<{
    id: number;
    clientId: string;
    previewUrl: string;
    isCover?: boolean;
    file?: {
      lgUrl?: string;
      mdUrl?: string;
      smUrl?: string;
      url?: string;
    };
  }> = []

  availableFeatureTags: string[] = []
  availableActivityTags: string[] = []
  selectedFeatureTags: string[] = []
  selectedActivityTags: string[] = []

  // Key-value route structure with per-route undo/redo
  routes: {
    [routeKey: string]: Route
  } = {}
  activeRouteKey: string | null = null

  // Legacy properties removed - use activeRoute instead

  isSnap: boolean = true
  center: number[] = [0, 0]
  zoom: number = 0
  miniCoordsBar: string = ''
  error?: string | null = undefined
  onMarkerMoveCalled = false;
  loading = false;
  clientId: string | null = null;
  shareableUrl: string | null = null;
  planMode: boolean = true;
  routePreference: string = "hike";
  hikeId: number | null = null;
  // Server id of the hike this plan was copied from ("Edit a Copy"). Sent as
  // originalHikeId on CreateHike so the backend propagates comments back to it.
  originalHikeId: number | null = null;
  trailDataId: number | null = null;
  tracerRouteGeoJson: any | null = null; // Stores GeoJSON for re-rendering on style change
  isHikeEdit: boolean = false;
  firstSymbolLayerId: string | null = null
  hoverPoint: { lng: number; lat: number; lineStringId: string; insertIndex: number } | null = null
  nearLine: boolean = false;
  _hoverMoveHandler: any = null;
  _hoverZoomHandler: any = null;
  _consolidatedHoverZoomHandler: any = null;
  mapHoverDistance: number | null = null;
  _hoverDistanceCache: { routeKey: string; lineStringCount: number; prefixLengths: number[] } | null = null;
  
  // Additional state properties
  routeCounter: number = 0;
  selectedMarker: MarkerFeature | null = null;
  selectedLineString: LineStringFeature | null = null;
  showModal: string = '';
  removalMode: boolean = false;
  
  // Day information for multi-day hikes
  dayInfo: {
    [dayNumber: number]: {
      name: string;
      description: string;
    }
  } = {};
  
  // Preview state for marker dragging
  dragPreview: {
    isDragging: boolean;
    markerIndex: number | null;
    routeId: string | null;
    originalPosition: [number, number] | null;
    previewPosition: [number, number] | null;
    snappedPosition: [number, number] | null;
    previewLineStrings: LineStringFeature[];
    pendingRequest: AbortController | null;
  } = {
    isDragging: false,
    markerIndex: null,
    routeId: null,
    originalPosition: null,
    previewPosition: null,
    snappedPosition: null,
    previewLineStrings: [],
    pendingRequest: null,
  };

  // Track pending provisional line replacements to prevent duplicate undo states
  pendingProvisionalReplacements: Set<string> = new Set();

  // Whether the 30km route length warning has been shown this session
  routeLengthWarningShown: boolean = false;

  // Version counter for routesWithStats caching — incremented when route geometry changes
  _statsVersion: number = 0;

  // Command-pattern undo manager (session-only, not persisted to IndexedDB)
  undoManager: UndoManager = new UndoManager();

  // Area selection mode state
  isAreaSelecting: boolean = false;
  areaSelectedMarkers: Array<{ routeKey: string; markerIndex: number }> = [];
  
  // Hike highlights (custom POIs)
  hikeHighlights: Array<{
    clientId: string;
    type: string;
    title: string;
    description: string;
    coordinates: [number, number];
    dayNumber?: number;
    imageSignedIds?: Array<{
      signedId: string;
      clientId: string;
    }>;
    imagePreviewUrls?: string[];
    images?: Array<{
      id?: string | number;
      clientId: string;
      file?: {
        lgUrl?: string;
        smUrl?: string;
        mdUrl?: string;
        url?: string;
      };
      _destroy?: boolean;
    }>;
  }> = [];
}

declare global {
  interface Window {
    map: mapboxgl.Map;
  }
}

const state = new PlanTrailState()

const planTrailModule: Module<PlanTrailState, RootState> = {
  namespaced: true,
  state: state,
  actions: actions,
  mutations: mutations,
  getters: getters,
}

export default planTrailModule
